import { msg } from "@lingui/core/macro";
import {
  addOrderLineMutation,
  applyOrderDiscountMutation,
  createOrderMutation,
  formatMoney,
  getCategoriesQuery,
  getCombosQuery,
  getCustomersQuery,
  getEmployeesQuery,
  getModifierOptionsQuery,
  getOpenSessionQuery,
  getOrderByIdQuery,
  getOrderLinesQuery,
  getPaymentsForOrderQuery,
  getProductModifierGroupsQuery,
  getProductsQuery,
  getTablesQuery,
  getVariantsQuery,
  parseMoneyToCents,
  payOrderCashMutation,
  queryKeys,
  redeemPoints,
  redeemPointsForOrderMutation,
  remainingDue,
  removeOrderLineMutation,
  reopenOrderMutation,
  restoreOrderLineMutation,
  sendOrderToKitchenMutation,
  setOrderCustomerMutation,
  setTableStatusMutation,
  splitPaymentMutation,
  voidOrderMutation,
} from "@comandero/shared";
import type {
  Branch,
  Combo,
  Customer,
  DiningTable,
  Employee,
  MenuCategory,
  MenuProduct,
  ModifierOption,
  Order,
  OrderLine,
  Organization,
  Payment,
  ProductModifierGroup,
  ProductVariant,
  RegisterSession,
} from "@comandero/powersync";
import { createPowerSyncQuery } from "@comandero/shared/solid";
import {
  type Accessor,
  createEffect,
  createMemo,
  createSignal,
  For,
  Show,
  untrack,
} from "solid-js";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Select, toSelectOptions } from "@/components/ui/select";
import { TextField } from "@/components/ui/text-field";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { useUndo } from "@/lib/undo";
import { EntityHistoryDialog } from "@/features/activity/components/entity-history-dialog";
import { ReceiptDialog } from "./receipt-dialog";
import { useMenuCategorySwipe } from "../hooks/use-menu-category-swipe";
import { useTicketLineSwipe } from "../hooks/use-ticket-line-swipe";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { createInitialSearchParam } from "@/lib/initial-search";

interface PosTerminalProps {
  org: () => Organization | null;
  branch: () => Branch | null;
  /** Orden activa proveniente de la URL (`?order=`) — deep-link compartible. */
  orderParam?: Accessor<string | undefined>;
  /** Categoría activa proveniente de la URL (`?category=`). */
  categoryParam?: Accessor<string | undefined>;
  /** Escribe la orden activa a la URL (idempotente, `replace`). */
  onOrderChange?: (orderId: string | null) => void;
  /** Escribe la categoría activa a la URL (idempotente, `replace`). */
  onCategoryChange?: (categoryId: string | null) => void;
}

export function PosTerminal(props: PosTerminalProps) {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const { db } = usePowerSyncContext();

  // Persistimos el id de la orden activa para que una recarga del POS la
  // restaure (la cola CRUD ya persistió la orden en SQLite). Así el ticket y el
  // estado "Paid" sobreviven a un reload, no solo la fila en la BD.
  const ACTIVE_ORDER_KEY = "comandero.activeOrderId";
  const readActiveOrder = (): string | null => {
    try {
      return globalThis.localStorage?.getItem(ACTIVE_ORDER_KEY) ?? null;
    } catch {
      return null;
    }
  };

  // Fuente de verdad de la orden activa = la URL (`?order=`), para que el ticket
  // sea un deep-link compartible. Si la URL no trae orden (entrada directa a
  // `/pos`), caemos al puntero persistido en localStorage. Capturamos el
  // `?order` inicial de forma síncrona para que el primer flush diferido del
  // router no lo pise (ver lib/initial-search.ts).
  const initialOrderFromUrl = createInitialSearchParam("order");
  // Semilla síncrona: el `?order` capturado de la URL gana sobre el puntero
  // persistido. El `props.orderParam` reactivo lo gobierna luego vía createEffect
  // (leerlo aquí, fuera de un scope rastreado, sería un no-op reactivo).
  const [orderId, setOrderIdRaw] = createSignal<string | null>(
    initialOrderFromUrl() ?? readActiveOrder(),
  );
  const setOrderId = (id: string | null) => {
    try {
      if (id) {
        globalThis.localStorage?.setItem(ACTIVE_ORDER_KEY, id);
      } else {
        globalThis.localStorage?.removeItem(ACTIVE_ORDER_KEY);
      }
    } catch {
      /* almacenamiento no disponible: seguimos en memoria */
    }
    setOrderIdRaw(id);
    // Refleja la orden activa en la URL (idempotente: no inflar history).
    props.onOrderChange?.(id);
  };

  // Back/forward o un enlace pegado en caliente: si la URL cambia de orden por
  // fuera (no por nuestra escritura), sembramos el signal con ese valor.
  // CC-16: `orderId()` se lee en `untrack` — el efecto debe depender SOLO de la
  // URL. Si trackeara la señal que escribe, un `setOrderId(B)` del usuario lo
  // re-dispararía con la URL aún en A y rebotaría el ticket al valor viejo.
  createEffect(() => {
    const fromUrl = props.orderParam?.();
    if (fromUrl !== undefined && fromUrl !== untrack(orderId)) {
      setOrderIdRaw(fromUrl);
      try {
        globalThis.localStorage?.setItem(ACTIVE_ORDER_KEY, fromUrl);
      } catch {
        /* sin almacenamiento: seguimos en memoria */
      }
    }
  });
  const [orderType, setOrderType] = createSignal<"dine_in" | "takeaway">("dine_in");
  const [selectedProduct, setSelectedProduct] = createSignal<MenuProduct | null>(null);
  const [selectedOption, setSelectedOption] = createSignal<ModifierOption | null>(null);
  const [tendered, setTendered] = createSignal("");
  const [changeSignal, setChange] = createSignal<number | null>(null);
  // Split bill + tips.
  const [splitAmount, setSplitAmount] = createSignal("");
  const [splitTip, setSplitTip] = createSignal("");
  const [sentToKitchen, setSentToKitchen] = createSignal(false);
  // Atribución de venta: el empleado que cobra (para el reporte por mesero).
  const [employeeId, setEmployeeId] = createSignal("");
  // F4: variante elegida del producto + cliente para fidelización.
  const [selectedVariant, setSelectedVariant] = createSignal<ProductVariant | null>(null);
  const [customerId, setCustomerId] = createSignal("");
  const [redeemInput, setRedeemInput] = createSignal("");
  // ACTIVITY-CORE (POS): operaciones de negocio registradas con actor+motivo.
  const [voidOrderOpen, setVoidOrderOpen] = createSignal(false);
  const [voidReason, setVoidReason] = createSignal("");
  const [discountOpen, setDiscountOpen] = createSignal(false);
  const [discountAmount, setDiscountAmount] = createSignal("");
  const [discountReason, setDiscountReason] = createSignal("");
  const [voidLineTarget, setVoidLineTarget] = createSignal<OrderLine | null>(null);
  const [voidLineReason, setVoidLineReason] = createSignal("");
  const [historyOpen, setHistoryOpen] = createSignal(false);
  // Ticket imprimible (ESC/POS): preview + descarga del payload.
  const [receiptOpen, setReceiptOpen] = createSignal(false);
  const { registerUndo } = useUndo();

  const orgId = (): string | null => props.org()?.id ?? null;
  const branchId = (): string | null => props.branch()?.id ?? null;

  const products = createPowerSyncQuery<MenuProduct>({
    queryKey: () => queryKeys.comandero.products(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getProductsQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  const categories = createPowerSyncQuery<MenuCategory>({
    queryKey: () => queryKeys.comandero.categories(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getCategoriesQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  // Category tabs: a leading "All" page, then one page per category. The active
  // page filters the product grid and is paged either by tapping a tab or by
  // swiping the grid (carousel). `id === null` is the "All" page.
  const initialCategoryFromUrl = createInitialSearchParam("category");
  const [categoryPageRaw, setCategoryPageRaw] = createSignal(0);
  const categoryPages = createMemo<{ id: string | null; name: string }[]>(() => [
    { id: null, name: t(msg`All`) },
    ...(categories.data ?? []).map((c) => ({ id: c.id, name: c.name ?? "" })),
  ]);

  // La categoría activa es deep-linkable por su id (`?category=`). Resolvemos id
  // → índice de página cuando las categorías ya cargaron; mientras tanto el
  // signal crudo manda. Idempotente: recargar la URL reconstruye la pestaña.
  const categoryPage: Accessor<number> = createMemo(() => {
    const fromUrl = props.categoryParam?.() ?? initialCategoryFromUrl();
    if (fromUrl) {
      const idx = categoryPages().findIndex((p) => p.id === fromUrl);
      if (idx !== -1) {
        return idx;
      }
    }
    return categoryPageRaw();
  });
  const setCategoryPage = (i: number) => {
    setCategoryPageRaw(i);
    const id = categoryPages()[i]?.id ?? null;
    props.onCategoryChange?.(id);
  };
  // Clamp the active page if categories change underneath it (e.g. deletion).
  const activeCategoryId = createMemo<string | null>(() => {
    const pages = categoryPages();
    const page = pages[Math.min(categoryPage(), pages.length - 1)];
    return page?.id ?? null;
  });
  const visibleProducts = createMemo<MenuProduct[]>(() => {
    const all = products.data ?? [];
    const catId = activeCategoryId();
    return catId === null ? all : all.filter((p) => p.category_id === catId);
  });

  const employees = createPowerSyncQuery<Employee>({
    queryKey: () => queryKeys.comandero.employees(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getEmployeesQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  // Modifier groups linked to the selected product → option list for selection.
  const linkedGroups = createPowerSyncQuery<ProductModifierGroup>({
    queryKey: () => queryKeys.comandero.productModifierGroups(selectedProduct()?.id ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      const p = selectedProduct();
      return d && id && p?.id ? getProductModifierGroupsQuery(d, id, p.id) : null;
    },
    db,
    enabled: () => Boolean(selectedProduct()?.id),
  });

  const firstGroupId = createMemo<string | null>(
    () => linkedGroups.data?.[0]?.modifier_group_id ?? null,
  );

  const options = createPowerSyncQuery<ModifierOption>({
    queryKey: () => queryKeys.comandero.modifierOptions(firstGroupId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      const g = firstGroupId();
      return d && id && g ? getModifierOptionsQuery(d, id, g) : null;
    },
    db,
    enabled: () => Boolean(firstGroupId()),
  });

  // F4: variantes del producto seleccionado (tamaños con precio propio).
  const variants = createPowerSyncQuery<ProductVariant>({
    queryKey: () => queryKeys.comandero.variants(selectedProduct()?.id ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const p = selectedProduct();
      return d && p?.id ? getVariantsQuery(d, p.id) : null;
    },
    db,
    enabled: () => Boolean(selectedProduct()?.id),
  });

  // F4: combos disponibles (pseudo-productos a precio fijo).
  const combos = createPowerSyncQuery<Combo>({
    queryKey: () => queryKeys.comandero.combos(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getCombosQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  // F4: clientes para fidelización.
  const customers = createPowerSyncQuery<Customer>({
    queryKey: () => queryKeys.comandero.customers(orgId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const id = orgId();
      return d && id ? getCustomersQuery(d, id) : null;
    },
    db,
    enabled: () => Boolean(orgId()),
  });

  const lines = createPowerSyncQuery<OrderLine>({
    queryKey: () => queryKeys.comandero.orderLines(orderId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const oid = orderId();
      return d && oid ? getOrderLinesQuery(d, oid) : null;
    },
    db,
    enabled: () => Boolean(orderId()),
  });

  const currentOrder = createPowerSyncQuery<Order>({
    queryKey: () => queryKeys.comandero.order(orderId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const oid = orderId();
      return d && oid ? getOrderByIdQuery(d, oid) : null;
    },
    db,
    enabled: () => Boolean(orderId()),
  });

  const payments = createPowerSyncQuery<Payment>({
    queryKey: () => queryKeys.comandero.orderPayments(orderId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const oid = orderId();
      return d && oid ? getPaymentsForOrderQuery(d, oid) : null;
    },
    db,
    enabled: () => Boolean(orderId()),
  });

  const order = createMemo<Order | null>(() => currentOrder.data?.[0] ?? null);
  const isPaid = createMemo(() => order()?.status === "paid");

  // Mesas de la sucursal: para imprimir el nombre visible de la mesa en el ticket.
  const branchTables = createPowerSyncQuery<DiningTable>({
    queryKey: () => queryKeys.comandero.tables(branchId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const oid0 = orgId();
      const bid = branchId();
      return d && oid0 && bid ? getTablesQuery(d, oid0, bid) : null;
    },
    db,
    enabled: () => Boolean(branchId()),
  });
  const receiptTableName = createMemo<string | null>(() => {
    const tid = order()?.table_id;
    if (!tid) {
      return null;
    }
    return (branchTables.data ?? []).find((tbl) => tbl.id === tid)?.name ?? null;
  });
  const receiptEmployeeName = createMemo<string | null>(() => {
    const eid = order()?.employee_id;
    if (!eid) {
      return null;
    }
    return (employees.data ?? []).find((emp) => emp.id === eid)?.name ?? null;
  });

  // Turno activo (sesión de caja abierta de la sucursal): las órdenes nuevas
  // nacen ligadas a él para que TODO el rastro (líneas, anulaciones, descuentos,
  // pagos) quede registrado POR TURNO (vista de cuadre en /activity?session=).
  const openSession = createPowerSyncQuery<RegisterSession>({
    queryKey: () => queryKeys.comandero.openSession(branchId() ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const oid0 = orgId();
      const bid = branchId();
      return d && oid0 && bid ? getOpenSessionQuery(d, oid0, bid) : null;
    },
    db,
    enabled: () => Boolean(branchId()),
  });

  // El ticket activo se restaura desde localStorage tras un reload. Si ese id
  // apunta a una orden borrada / aún no sincronizada a este dispositivo / de otro
  // restaurante, NO debe bloquear el POS: un puntero de ticket restaurado que ya
  // no resuelve se DESCARTA y volvemos a "Start a new order" (un POS siempre puede
  // iniciar una orden nueva). Sin esto, tras crear un restaurante con un puntero
  // viejo en localStorage el POS se quedaba en "Order not found" en vez de mostrar
  // el inicio de orden.
  const orderResolution = createEntityResolution({
    db: () => db(),
    hasEntity: () => Boolean(order()),
    isPending: () => currentOrder.isLoading,
  });
  createEffect(() => {
    if (!orderId()) {
      return;
    }
    // Aislamiento multi-restaurante: el puntero de ticket activo es GLOBAL
    // (una sola clave en localStorage / `?order=`), pero una orden pertenece a
    // UN restaurante. Tras cambiar de restaurante en el switcher, ese puntero
    // resolvería a una orden del restaurante ANTERIOR (sus datos siguen en
    // local) y el POST del restaurante activo mostraría —y cobraría contra la
    // caja de— una orden ajena. Si la orden resuelta no es del `org()` activo
    // se descarta como si no existiera (un POS siempre puede empezar de nuevo).
    const resolved = order();
    const activeOrg = orgId();
    if (resolved && activeOrg && resolved.organization_id !== activeOrg) {
      setOrderId(null);
      setChange(null);
      return;
    }
    const state = orderResolution();
    if (state === "not-found" || state === "offline") {
      setOrderId(null);
      setChange(null);
    }
  });

  // El cambio mostrado: el de la acción de cobro recién hecha, o (tras recarga)
  // el `change_given` del pago persistido.
  const change = createMemo<number | null>(() => {
    const live = changeSignal();
    if (live !== null) {
      return live;
    }
    return payments.data?.[0]?.change_given ?? null;
  });

  async function startOrder() {
    const d = drizzle();
    const oid0 = orgId();
    const bid = branchId();
    if (!d || !oid0 || !bid) {
      return;
    }
    const id = await createOrderMutation(d, oid0, bid, {
      type: orderType(),
      // Liga la orden al turno abierto (registro por caja, antifraude).
      registerSessionId: openSession.data?.[0]?.id ?? null,
    });
    setOrderId(id);
    setChange(null);
  }

  async function addSelected() {
    const d = drizzle();
    const oid0 = orgId();
    const oid = orderId();
    const p = selectedProduct();
    if (!d || !oid0 || !oid || !p) {
      return;
    }
    const opt = selectedOption();
    // F4: si hay variante elegida, su precio ABSOLUTO reemplaza el base y el
    // nombre de línea refleja la variante (p.ej. "Coke (Large)").
    const variant = selectedVariant();
    const unitPrice = variant?.price ?? p.price ?? 0;
    const lineName = variant ? `${p.name} (${variant.name})` : (p.name ?? "");
    await addOrderLineMutation(
      d,
      oid0,
      oid,
      { id: p.id, name: lineName, unitPrice },
      {
        qty: 1,
        taxRate: p.tax_rate ?? 0,
        modifiers: opt
          ? [{ optionId: opt.id, name: opt.name ?? "", priceDelta: opt.price_delta ?? 0 }]
          : [],
      },
    );
    setSelectedProduct(null);
    setSelectedOption(null);
    setSelectedVariant(null);
  }

  /** F4: agrega un combo como línea a precio fijo del combo. */
  async function addCombo(combo: Combo) {
    const d = drizzle();
    const oid0 = orgId();
    const oid = orderId();
    if (!d || !oid0 || !oid) {
      return;
    }
    await addOrderLineMutation(
      d,
      oid0,
      oid,
      { id: null, name: combo.name ?? "", unitPrice: combo.price ?? 0 },
      { qty: 1, taxRate: combo.tax_rate ?? 0 },
    );
  }

  /** F4: asocia un cliente a la orden (para acumular puntos al cobrar). */
  async function assignCustomer(id: string) {
    setCustomerId(id);
    const d = drizzle();
    const oid = orderId();
    if (!d || !oid) {
      return;
    }
    await setOrderCustomerMutation(d, oid, id || null);
  }

  /** F4: canjea puntos del cliente como descuento de la orden. */
  async function applyRedeem() {
    const d = drizzle();
    const oid0 = orgId();
    const oid = orderId();
    const cid = customerId();
    if (!d || !oid0 || !oid || !cid) {
      return;
    }
    const cust = (customers.data ?? []).find((c) => c.id === cid);
    const requested = Math.trunc(Number(redeemInput() || "0"));
    const total = order()?.total ?? 0;
    const { pointsToRedeem, discountCents } = redeemPoints(requested, cust?.points ?? 0, total);
    if (pointsToRedeem <= 0) {
      return;
    }
    await redeemPointsForOrderMutation(d, oid0, oid, cid, pointsToRedeem, discountCents);
    setRedeemInput("");
  }

  async function charge() {
    const d = drizzle();
    const oid0 = orgId();
    const oid = orderId();
    if (!d || !oid0 || !oid) {
      return;
    }
    const cents = parseMoneyToCents(tendered());
    const result = await payOrderCashMutation(d, oid0, oid, cents, {
      method: "cash",
      employeeId: employeeId() || null,
    });
    setChange(result.change);
    const tableId = order()?.table_id ?? null;
    if (tableId) {
      await setTableStatusMutation(d, tableId, "free");
    }
  }

  // Saldo pendiente de la cuenta (total − pagos registrados), en centavos.
  const remaining = createMemo<number>(() => {
    const total = order()?.total ?? 0;
    const paid = (payments.data ?? []).map((p) => p.amount ?? 0);
    return remainingDue(total, paid);
  });

  async function sendToKitchen() {
    const d = drizzle();
    const oid = orderId();
    if (!d || !oid) {
      return;
    }
    await sendOrderToKitchenMutation(d, oid);
    setSentToKitchen(true);
  }

  /**
   * Quita una línea del ticket (botón o swipe-to-remove).
   * POLÍTICA POS: en una orden ABIERTA es una edición normal (con Deshacer L1);
   * en una orden YA COMANDADA (`sent`) es una ANULACIÓN — pide motivo y queda
   * registrada con actor en el historial (sin undo).
   */
  function removeLine(line: OrderLine) {
    // `sentToKitchen()` cubre la ventana entre comandar y que el row local
    // refleje status="sent" (la señal optimista es la intención del usuario).
    if (order()?.status === "sent" || sentToKitchen()) {
      setVoidLineReason("");
      setVoidLineTarget(line);
      return;
    }
    void (async () => {
      const d = drizzle();
      const oid0 = orgId();
      const oid = orderId();
      if (!d || !oid0 || !oid) {
        return;
      }
      await removeOrderLineMutation(d, oid0, oid, line.id);
      // eslint-disable-next-line lingui/no-single-variables-to-translate -- la frase tiene texto; {name} es placeholder ICU
      registerUndo(t(msg`Item removed: {name}`, { name: line.name ?? "" }), async () => {
        await restoreOrderLineMutation(d, oid0, oid, line.id);
      });
    })();
  }

  /** Confirma la anulación de una línea comandada (motivo opcional). */
  async function confirmVoidLine() {
    const d = drizzle();
    const oid0 = orgId();
    const oid = orderId();
    const line = voidLineTarget();
    if (!d || !oid0 || !oid || !line) {
      return;
    }
    await removeOrderLineMutation(d, oid0, oid, line.id, {
      reason: voidLineReason() || undefined,
    });
    setVoidLineTarget(null);
  }

  /** Anula la orden completa (motivo opcional; libera la mesa). */
  async function confirmVoidOrder() {
    const d = drizzle();
    const oid = orderId();
    if (!d || !oid) {
      return;
    }
    await voidOrderMutation(d, oid, voidReason() || undefined);
    const tableId = order()?.table_id ?? null;
    if (tableId) {
      await setTableStatusMutation(d, tableId, "free");
    }
    setVoidOrderOpen(false);
    setOrderId(null);
    setChange(null);
  }

  /** Aplica un descuento de ticket (monto + motivo opcional). */
  async function confirmDiscount() {
    const d = drizzle();
    const oid0 = orgId();
    const oid = orderId();
    if (!d || !oid0 || !oid) {
      return;
    }
    const cents = parseMoneyToCents(discountAmount());
    await applyOrderDiscountMutation(d, oid0, oid, cents, discountReason() || undefined);
    setDiscountOpen(false);
    setDiscountAmount("");
    setDiscountReason("");
  }

  /** Reabre una orden pagada (operación registrada, NO un undo). */
  async function reopenOrder() {
    const d = drizzle();
    const oid = orderId();
    if (!d || !oid) {
      return;
    }
    await reopenOrderMutation(d, oid);
    setChange(null);
  }

  /** Registra un pago parcial (split) con propina opcional. */
  async function paySplit() {
    const d = drizzle();
    const oid0 = orgId();
    const oid = orderId();
    if (!d || !oid0 || !oid) {
      return;
    }
    const amount = parseMoneyToCents(splitAmount());
    const tip = parseMoneyToCents(splitTip());
    if (amount <= 0) {
      return;
    }
    const result = await splitPaymentMutation(d, oid0, oid, amount, {
      method: "cash",
      tip,
      employeeId: employeeId() || null,
    });
    setSplitAmount("");
    setSplitTip("");
    if (result.settled) {
      setChange(result.change);
      const tableId = order()?.table_id ?? null;
      if (tableId) {
        await setTableStatusMutation(d, tableId, "free");
      }
    }
  }

  return (
    <div class="flex flex-col gap-6" data-testid="pos-terminal">
      <Show
        when={orderId()}
        fallback={
          <div class="flex flex-col items-center justify-center gap-5 rounded-2xl border border-dashed border-border/60 bg-surface/40 px-6 py-16 text-center">
            <div class="flex h-16 w-16 items-center justify-center rounded-2xl bg-accent/12 text-accent">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                class="h-8 w-8"
                aria-hidden="true"
              >
                <path d="M12 5v14M5 12h14" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
            <div>
              <p class="text-lg font-semibold text-foreground">{t(msg`Start a new order`)}</p>
              <p class="mt-1 text-sm text-muted-foreground">
                {t(msg`Pick the order type and tap to begin.`)}
              </p>
            </div>
            <div class="flex flex-wrap items-center justify-center gap-3">
              <Select
                data-testid="order-type-select"
                aria-label={t(msg`Order type`)}
                size="lg"
                value={orderType()}
                onChange={(v) => setOrderType(v === "takeaway" ? "takeaway" : "dine_in")}
                options={[
                  { value: "dine_in", label: t(msg`Dine-in`) },
                  { value: "takeaway", label: t(msg`Takeaway`) },
                ]}
              />
              <Button size="touch" data-testid="new-order-btn" onClick={() => void startOrder()}>
                {t(msg`New order`)}
              </Button>
            </div>
          </div>
        }
      >
        <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Product grid */}
          <section>
            <h3 class="mb-2 text-sm font-semibold text-muted-foreground">{t(msg`Products`)}</h3>
            <Show when={categoryPages().length > 1}>
              <ProductCategoryTabs
                pages={categoryPages}
                active={categoryPage}
                onSelect={(i) => {
                  setCategoryPage(i);
                  setSelectedProduct(null);
                }}
              />
            </Show>
            <ProductGrid
              pages={categoryPages}
              activePage={categoryPage}
              setActivePage={(i) => {
                setCategoryPage(i);
                setSelectedProduct(null);
              }}
              products={visibleProducts}
              selectedId={() => selectedProduct()?.id ?? null}
              onPick={(p) => {
                setSelectedProduct(p);
                setSelectedOption(null);
                setSelectedVariant(null);
              }}
            />

            {/* F4: combos a precio fijo — un toque agrega la línea */}
            <Show when={(combos.data ?? []).length > 0}>
              <div class="mt-3" data-testid="combo-picker">
                <p class="mb-1 text-xs text-muted-foreground">{t(msg`Combos`)}</p>
                <div class="flex flex-wrap gap-2">
                  <For each={combos.data}>
                    {(c) => (
                      <button
                        type="button"
                        data-testid="pos-combo"
                        data-combo-name={c.name}
                        class="flex min-h-16 flex-col justify-between rounded-xl border border-border/60 bg-surface px-3.5 py-2.5 text-left transition-[transform,border-color,background-color] hover:border-accent/60 hover:bg-accent/5 active:scale-[0.98]"
                        onClick={() => void addCombo(c)}
                      >
                        <div class="text-sm font-semibold">{c.name}</div>
                        <div class="mt-1 font-numeric text-sm font-bold text-accent">
                          {formatMoney(c.price)}
                        </div>
                      </button>
                    )}
                  </For>
                </div>
              </div>
            </Show>

            <Show when={selectedProduct()}>
              <div class="mt-3" data-testid="modifier-picker">
                {/* F4: variantes (tamaños con precio propio) */}
                <Show when={(variants.data ?? []).length > 0}>
                  <p class="mb-1 text-xs text-muted-foreground">{t(msg`Variant`)}</p>
                  <div class="mb-2 flex flex-wrap gap-2">
                    <For each={variants.data}>
                      {(v) => (
                        <button
                          type="button"
                          data-testid="variant-option"
                          data-variant-name={v.name}
                          class="rounded-md border border-border/40 px-2 py-1 text-xs"
                          classList={{ "ring-2 ring-ring": selectedVariant()?.id === v.id }}
                          onClick={() => setSelectedVariant(v)}
                        >
                          {v.name} ({formatMoney(v.price)})
                        </button>
                      )}
                    </For>
                  </div>
                </Show>
                <Show when={(options.data ?? []).length > 0}>
                  <p class="mb-1 text-xs text-muted-foreground">{t(msg`Modifier`)}</p>
                  <div class="flex flex-wrap gap-2">
                    <For each={options.data}>
                      {(o) => (
                        <button
                          type="button"
                          data-testid="modifier-option"
                          data-option-name={o.name}
                          class="rounded-md border border-border/40 px-2 py-1 text-xs"
                          classList={{ "ring-2 ring-ring": selectedOption()?.id === o.id }}
                          onClick={() => setSelectedOption(o)}
                        >
                          {o.name} (+{formatMoney(o.price_delta)})
                        </button>
                      )}
                    </For>
                  </div>
                </Show>
                <Button
                  size="touch"
                  class="mt-3 w-full"
                  data-testid="add-line-btn"
                  onClick={() => void addSelected()}
                >
                  {t(msg`Add to order`)}
                </Button>
              </div>
            </Show>
          </section>

          {/* Ticket */}
          <section class="rounded-2xl border border-border/60 bg-surface p-4 shadow-sm shadow-black/5">
            <h3 class="mb-3 text-sm font-semibold text-muted-foreground">{t(msg`Ticket`)}</h3>
            <ul class="flex flex-col divide-y divide-border/40" data-testid="ticket-lines">
              <Show
                when={(lines.data ?? []).length > 0}
                fallback={
                  <li class="py-6 text-center text-sm text-muted-foreground">
                    {t(msg`No items yet. Tap a product to add it.`)}
                  </li>
                }
              >
                <For each={lines.data}>
                  {(l) => (
                    <TicketLine
                      line={l}
                      canRemove={() => !isPaid()}
                      onRemove={() => removeLine(l)}
                    />
                  )}
                </For>
              </Show>
            </ul>
            <div class="mt-3 space-y-1.5 border-t border-border/60 pt-3 text-sm">
              <div class="flex justify-between text-muted-foreground">
                <span>{t(msg`Subtotal`)}</span>
                <span class="font-numeric tabular-nums" data-testid="order-subtotal">
                  {formatMoney(order()?.subtotal)}
                </span>
              </div>
              <div class="flex justify-between text-muted-foreground">
                <span>{t(msg`Tax`)}</span>
                <span class="font-numeric tabular-nums" data-testid="order-tax">
                  {formatMoney(order()?.tax_total)}
                </span>
              </div>
              <div class="flex items-baseline justify-between pt-1 text-lg font-bold">
                <span>{t(msg`Total`)}</span>
                <span class="font-numeric tabular-nums text-accent" data-testid="order-total">
                  {formatMoney(order()?.total)}
                </span>
              </div>
              <div
                class="flex justify-between text-sm text-muted-foreground"
                data-testid="order-remaining-row"
              >
                <span>{t(msg`Remaining`)}</span>
                <span class="font-numeric tabular-nums" data-testid="order-remaining">
                  {formatMoney(remaining())}
                </span>
              </div>
            </div>

            <Show when={!isPaid() && (lines.data ?? []).length > 0}>
              <div class="mt-3">
                <Button
                  variant="secondary"
                  size="touch"
                  class="w-full"
                  data-testid="send-to-kitchen-btn"
                  disabled={sentToKitchen() || order()?.status === "sent"}
                  onClick={() => void sendToKitchen()}
                >
                  <Show
                    when={sentToKitchen() || order()?.status === "sent"}
                    fallback={t(msg`Send to kitchen`)}
                  >
                    <span data-testid="sent-to-kitchen-badge">{t(msg`Sent to kitchen`)}</span>
                  </Show>
                </Button>
              </div>
            </Show>

            {/* ACTIVITY-CORE (POS): operaciones de negocio registradas con actor. */}
            <Show when={!isPaid() && order()}>
              <div class="mt-3 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  class="flex-1"
                  data-testid="order-discount-btn"
                  onClick={() => setDiscountOpen(true)}
                >
                  {t(msg`Discount`)}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  class="flex-1 text-destructive"
                  data-testid="order-void-btn"
                  onClick={() => {
                    setVoidReason("");
                    setVoidOrderOpen(true);
                  }}
                >
                  {t(msg`Void order`)}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="order-history-btn"
                  onClick={() => setHistoryOpen(true)}
                >
                  {t(msg`History`)}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="print-ticket-btn"
                  onClick={() => setReceiptOpen(true)}
                >
                  {t(msg`Ticket`)}
                </Button>
              </div>
            </Show>

            <Show
              when={!isPaid()}
              fallback={
                <div
                  class="mt-4 rounded-xl border border-success/40 bg-success/10 p-4 text-sm"
                  data-testid="paid-banner"
                >
                  <div class="flex items-center gap-2 text-base font-bold text-success">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.5"
                      class="h-5 w-5"
                      aria-hidden="true"
                    >
                      <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    {t(msg`Paid`)}
                  </div>
                  <div class="mt-1">
                    {t(msg`Change`)}:{" "}
                    <span
                      class="font-numeric tabular-nums font-semibold"
                      data-testid="change-amount"
                    >
                      {formatMoney(change())}
                    </span>
                  </div>
                  <Button
                    size="touch"
                    class="mt-3 w-full"
                    data-testid="new-order-after-paid-btn"
                    onClick={() => {
                      setOrderId(null);
                      setChange(null);
                      setCustomerId("");
                      setRedeemInput("");
                      setSentToKitchen(false);
                    }}
                  >
                    {t(msg`New order`)}
                  </Button>
                  {/* Reapertura EXPLÍCITA (no undo): queda en el historial con actor. */}
                  <div class="mt-2 flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      class="flex-1"
                      data-testid="reopen-order-btn"
                      onClick={() => void reopenOrder()}
                    >
                      {t(msg`Reopen order`)}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="order-history-btn-paid"
                      onClick={() => setHistoryOpen(true)}
                    >
                      {t(msg`History`)}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="print-ticket-btn-paid"
                      onClick={() => setReceiptOpen(true)}
                    >
                      {t(msg`Print ticket`)}
                    </Button>
                  </div>
                </div>
              }
            >
              {/* Atribución de venta al empleado que cobra (reporte por mesero). */}
              <Show when={(employees.data ?? []).length > 0}>
                <div class="mt-4">
                  <p class="mb-1 text-xs text-muted-foreground">{t(msg`Sold by`)}</p>
                  <Select
                    data-testid="pos-employee-select"
                    aria-label={t(msg`Sold by`)}
                    fluid
                    value={employeeId()}
                    onChange={setEmployeeId}
                    options={[
                      { value: "", label: t(msg`Unassigned`) },
                      ...toSelectOptions(
                        employees.data,
                        (emp) => emp.id,
                        (emp) => emp.name,
                      ),
                    ]}
                  />
                </div>
              </Show>

              {/* F4: cliente + canje de puntos de fidelización */}
              <Show when={(customers.data ?? []).length > 0}>
                <div class="mt-4" data-testid="loyalty-section">
                  <p class="mb-1 text-xs text-muted-foreground">{t(msg`Customer`)}</p>
                  <Select
                    data-testid="pos-customer-select"
                    aria-label={t(msg`Customer`)}
                    fluid
                    value={customerId()}
                    onChange={(v) => void assignCustomer(v)}
                    options={[
                      { value: "", label: t(msg`Walk-in`) },
                      ...(customers.data ?? []).map((c) => ({
                        value: c.id,
                        label: `${c.name} (${c.points ?? 0} ${t(msg`pts`)})`,
                      })),
                    ]}
                  />
                  <Show when={customerId()}>
                    <div class="mt-2 flex items-center gap-2">
                      <TextField
                        data-testid="redeem-points-input"
                        placeholder={t(msg`Redeem points`)}
                        inputmode="numeric"
                        value={redeemInput()}
                        onInput={(e) => setRedeemInput(e.currentTarget.value)}
                      />
                      <Button
                        variant="secondary"
                        data-testid="redeem-points-btn"
                        onClick={() => void applyRedeem()}
                      >
                        {t(msg`Redeem`)}
                      </Button>
                    </div>
                  </Show>
                </div>
              </Show>

              <div class="mt-4 flex items-center gap-2">
                <TextField
                  data-testid="tendered-input"
                  placeholder={t(msg`Cash received`)}
                  inputmode="decimal"
                  value={tendered()}
                  onInput={(e) => setTendered(e.currentTarget.value)}
                />
                <Button size="touch" data-testid="charge-btn" onClick={() => void charge()}>
                  {t(msg`Charge cash`)}
                </Button>
              </div>

              {/* Split bill + tips: registra pagos parciales con propina. */}
              <div class="mt-4 border-t border-border/40 pt-3" data-testid="split-section">
                <p class="mb-2 text-xs font-semibold text-muted-foreground">
                  {t(msg`Split bill / tip`)}
                </p>
                <div class="flex flex-wrap items-center gap-2">
                  <TextField
                    data-testid="split-amount-input"
                    placeholder={t(msg`Amount`)}
                    inputmode="decimal"
                    value={splitAmount()}
                    onInput={(e) => setSplitAmount(e.currentTarget.value)}
                  />
                  <TextField
                    data-testid="split-tip-input"
                    placeholder={t(msg`Tip`)}
                    inputmode="decimal"
                    value={splitTip()}
                    onInput={(e) => setSplitTip(e.currentTarget.value)}
                  />
                  <Button
                    variant="secondary"
                    data-testid="split-pay-btn"
                    onClick={() => void paySplit()}
                  >
                    {t(msg`Add payment`)}
                  </Button>
                </div>
              </div>
            </Show>
          </section>
        </div>
      </Show>

      {/* ── ACTIVITY-CORE: diálogos de operaciones registradas ───────────── */}
      {/* Anular ORDEN (motivo opcional; queda en el historial con actor). */}
      <Dialog open={voidOrderOpen()} onOpenChange={setVoidOrderOpen} contentClass="max-w-sm">
        <div class="space-y-4" data-testid="void-order-dialog">
          <div>
            <h2 class="text-lg font-semibold">{t(msg`Void this order?`)}</h2>
            <p class="mt-1 text-sm text-muted-foreground">
              {t(
                msg`The order will be cancelled. This is recorded in the activity log with your name.`,
              )}
            </p>
          </div>
          <TextField
            value={voidReason()}
            onInput={(e) => setVoidReason(e.currentTarget.value)}
            placeholder={t(msg`Reason (optional)`)}
            data-testid="void-order-reason"
          />
          <div class="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setVoidOrderOpen(false)}>
              {t(msg`Cancel`)}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              data-testid="void-order-confirm"
              onClick={() => void confirmVoidOrder()}
            >
              {t(msg`Void order`)}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Anular LÍNEA comandada (motivo opcional). */}
      <Dialog
        open={Boolean(voidLineTarget())}
        onOpenChange={(open) => {
          if (!open) {
            setVoidLineTarget(null);
          }
        }}
        contentClass="max-w-sm"
      >
        <div class="space-y-4" data-testid="void-line-dialog">
          <div>
            <h2 class="text-lg font-semibold">
              {/* eslint-disable-next-line lingui/no-single-variables-to-translate -- la frase tiene texto; {name} es placeholder ICU */}
              {t(msg`Void "{name}"?`, { name: voidLineTarget()?.name ?? "" })}
            </h2>
            <p class="mt-1 text-sm text-muted-foreground">
              {t(
                msg`This item was already sent to the kitchen. Voiding it is recorded with your name.`,
              )}
            </p>
          </div>
          <TextField
            value={voidLineReason()}
            onInput={(e) => setVoidLineReason(e.currentTarget.value)}
            placeholder={t(msg`Reason (optional)`)}
            data-testid="void-line-reason"
          />
          <div class="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setVoidLineTarget(null)}>
              {t(msg`Cancel`)}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              data-testid="void-line-confirm"
              onClick={() => void confirmVoidLine()}
            >
              {t(msg`Void item`)}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Descuento de ticket (monto + motivo opcional). */}
      <Dialog open={discountOpen()} onOpenChange={setDiscountOpen} contentClass="max-w-sm">
        <div class="space-y-4" data-testid="discount-dialog">
          <div>
            <h2 class="text-lg font-semibold">{t(msg`Apply a discount`)}</h2>
            <p class="mt-1 text-sm text-muted-foreground">
              {t(msg`Discounts are recorded in the activity log with your name and reason.`)}
            </p>
          </div>
          <TextField
            value={discountAmount()}
            onInput={(e) => setDiscountAmount(e.currentTarget.value)}
            placeholder={t(msg`Amount`)}
            inputmode="decimal"
            data-testid="discount-amount"
          />
          <TextField
            value={discountReason()}
            onInput={(e) => setDiscountReason(e.currentTarget.value)}
            placeholder={t(msg`Reason (optional)`)}
            data-testid="discount-reason"
          />
          <div class="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setDiscountOpen(false)}>
              {t(msg`Cancel`)}
            </Button>
            <Button size="sm" data-testid="discount-confirm" onClick={() => void confirmDiscount()}>
              {t(msg`Apply discount`)}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Ticket imprimible: preview + payload ESC/POS descargable. */}
      <ReceiptDialog
        open={receiptOpen}
        onOpenChange={setReceiptOpen}
        order={order}
        lines={() => lines.data ?? []}
        payments={() => payments.data ?? []}
        restaurantName={() => props.org()?.name ?? ""}
        branchName={() => props.branch()?.name ?? null}
        tableName={receiptTableName}
        employeeName={receiptEmployeeName}
      />

      {/* Historial de la orden (sheet, revert solo si la política lo permite). */}
      <Show when={orderId()}>
        {(oid) => (
          <EntityHistoryDialog
            open={historyOpen()}
            onOpenChange={setHistoryOpen}
            entityType="order"
            entityId={oid()}
            canRevert={!isPaid()}
          />
        )}
      </Show>
    </div>
  );
}

/**
 * Una línea del ticket con swipe-to-remove (premium, consistente con el estándar
 * lib/gestures). Swipe-left compromete el borrado; un botón de borrar visible en
 * hover/focus mantiene el acceso por teclado y ratón intacto. Solo mientras la
 * orden no esté pagada.
 */
function TicketLine(props: { line: OrderLine; canRemove: () => boolean; onRemove: () => void }) {
  const { t } = useI18n();
  let surfaceRef: HTMLDivElement | undefined;

  const swipe = useTicketLineSwipe({
    enabled: () => props.canRemove(),
    onRemove: () => props.onRemove(),
    getSurfaceRef: () => surfaceRef,
  });

  return (
    <li
      class="relative animate-completed-reveal overflow-hidden"
      data-testid="ticket-line"
      data-line-id={props.line.id}
    >
      {/* Underlay: affordance de borrado que se revela al deslizar a la izquierda. */}
      <Show when={props.canRemove()}>
        <div
          class="pointer-events-none absolute inset-y-0 right-0 flex items-center justify-end gap-1.5 pr-3 text-sm font-semibold text-destructive"
          aria-hidden="true"
          style={{ opacity: swipe.tx() < 0 ? 1 : 0 }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            class="h-4 w-4"
          >
            <path
              d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          {t(msg`Remove`)}
        </div>
      </Show>

      <div
        ref={(el) => {
          surfaceRef = el;
        }}
        class="group flex items-center justify-between gap-3 bg-surface py-2 text-sm touch-pan-y"
        style={{
          transform: `translate3d(${swipe.tx()}px, 0, 0)`,
          transition: swipe.isAnimating()
            ? "transform 240ms cubic-bezier(0.32, 0.72, 0, 1)"
            : "none",
        }}
        onPointerDown={swipe.handlePointerDown}
        onPointerMove={swipe.handlePointerMove}
        onPointerUp={swipe.handlePointerUp}
        onPointerCancel={swipe.handlePointerCancel}
      >
        <span class="font-medium">
          <span class="font-numeric text-muted-foreground">{props.line.qty}×</span>{" "}
          {props.line.name}
        </span>
        <span class="flex items-center gap-2">
          <span class="font-numeric tabular-nums">{formatMoney(props.line.line_total)}</span>
          <Show when={props.canRemove()}>
            <button
              type="button"
              data-testid="remove-line-btn"
              aria-label={t(msg`Remove line`)}
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100"
              onClick={() => props.onRemove()}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                class="h-4 w-4"
                aria-hidden="true"
              >
                <path
                  d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </Show>
        </span>
      </div>
    </li>
  );
}

interface CategoryPage {
  id: string | null;
  name: string;
}

/**
 * Category tab strip for the POS product grid. The accessible, primary way to
 * change category (tap or keyboard); the grid swipe is an additive shortcut that
 * drives the same `onSelect`. Implemented as an ARIA tablist so arrow keys and
 * screen readers navigate the categories.
 */
function ProductCategoryTabs(props: {
  pages: Accessor<CategoryPage[]>;
  active: Accessor<number>;
  onSelect: (index: number) => void;
}) {
  const { t } = useI18n();
  return (
    <div
      role="tablist"
      aria-label={t(msg`Menu categories`)}
      class="-mx-1 mb-3 flex gap-2 overflow-x-auto px-1 pb-1"
      data-testid="pos-category-tabs"
    >
      <For each={props.pages()}>
        {(page, index) => {
          const isActive = () => props.active() === index();
          return (
            <button
              type="button"
              role="tab"
              aria-selected={isActive()}
              tabindex={isActive() ? 0 : -1}
              data-testid="pos-category-tab"
              data-category-name={page.name}
              class="shrink-0 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors"
              classList={{
                "border-accent bg-accent/10 text-accent": isActive(),
                "border-border/60 text-muted-foreground hover:border-accent/50 hover:text-foreground":
                  !isActive(),
              }}
              onClick={() => props.onSelect(index())}
              onKeyDown={(e) => {
                const last = props.pages().length - 1;
                if (e.key === "ArrowRight") {
                  e.preventDefault();
                  props.onSelect(Math.min(props.active() + 1, last));
                } else if (e.key === "ArrowLeft") {
                  e.preventDefault();
                  props.onSelect(Math.max(props.active() - 1, 0));
                }
              }}
            >
              {page.name}
            </button>
          );
        }}
      </For>
    </div>
  );
}

/**
 * The product grid as a horizontal carousel: swiping pages between category
 * tabs (premium, on the shared gesture standard) while taps still pick a
 * product. `touch-action: pan-y` keeps native vertical scroll; the recognizer
 * claims the horizontal axis. A swipe that engaged suppresses the resulting
 * product tap so paging never adds a line by accident.
 */
function ProductGrid(props: {
  pages: Accessor<CategoryPage[]>;
  activePage: Accessor<number>;
  setActivePage: (index: number) => void;
  products: Accessor<MenuProduct[]>;
  selectedId: Accessor<string | null>;
  onPick: (product: MenuProduct) => void;
}) {
  const { t } = useI18n();
  let surfaceRef: HTMLDivElement | undefined;

  const swipe = useMenuCategorySwipe({
    index: () => props.activePage(),
    count: () => props.pages().length,
    onChange: (next) => props.setActivePage(next),
    getSurfaceRef: () => surfaceRef,
  });

  return (
    <div
      ref={(el) => {
        surfaceRef = el;
      }}
      class="overflow-hidden"
      style={{ "touch-action": "pan-y" }}
      onPointerDown={swipe.handlePointerDown}
      onPointerMove={swipe.handlePointerMove}
      onPointerUp={swipe.handlePointerUp}
      onPointerCancel={swipe.handlePointerCancel}
    >
      <div
        class="grid grid-cols-2 gap-3 will-change-transform sm:grid-cols-3"
        style={{
          transform: `translate3d(${swipe.dragX()}px, 0, 0)`,
          transition: swipe.isAnimating() ? `transform ${swipe.TRANSITION_MS}ms ease-out` : "none",
        }}
      >
        <Show
          when={props.products().length > 0}
          fallback={
            <p
              class="col-span-full py-8 text-center text-sm text-muted-foreground"
              data-testid="pos-category-empty"
            >
              {t(msg`No products in this category.`)}
            </p>
          }
        >
          <For each={props.products()}>
            {(p) => (
              <button
                type="button"
                data-testid="pos-product"
                data-product-name={p.name}
                aria-pressed={props.selectedId() === p.id}
                class="flex min-h-20 flex-col justify-between rounded-xl border border-border/60 bg-surface p-3.5 text-left transition-[transform,border-color,background-color] hover:border-accent/60 hover:bg-accent/5 active:scale-[0.98]"
                classList={{
                  "ring-2 ring-accent border-accent/70 bg-accent/5": props.selectedId() === p.id,
                }}
                onClick={() => {
                  // A horizontal swipe captured the pointer; don't also pick.
                  if (swipe.isSwiping()) {
                    return;
                  }
                  props.onPick(p);
                }}
              >
                <div class="text-sm font-semibold leading-tight">{p.name}</div>
                <div class="mt-2 font-numeric text-sm font-bold text-accent">
                  {formatMoney(p.price)}
                </div>
              </button>
            )}
          </For>
        </Show>
      </div>
    </div>
  );
}
